import { Shimmer } from '@flexo/atoms';
import { IconWrapper, formatHumanDate, openDocument } from '@flexo/general';
import { ALERT, KPI } from '@flexo/reducers';
import { computeKwickInvoicesSiteIds } from '@flexo/resolvers';
import { Player } from '@lottiefiles/react-lottie-player';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { IBilling } from '../../organisms.interfaces';
import styles from './billing.module.scss';

type BillingItem = {
  id: string;
  emissionDate?: string;
  paid: boolean;
  file_base64?: string;
  siteID?: string;
  invoice_type?: string;
};

type KwickInvoice = {
  invoice_no?: string;
  invoiceID?: string;
  id?: string;
  emission_date?: string;
  is_paid?: boolean;
  paid?: boolean;
  file_base64?: string;
  siteId?: string;
  siteID?: string;
  invoice_type?: string;
};

type RootKpi = {
  KwickInvoicesAsyncStatus?: string;
  kwickInvoices?: KwickInvoice[];
  kwickOverview?: any;
  lastKwickSiteIds?: string[];
};

type RootState = {
  kpi?: RootKpi;
  metadata?: any;
};

const IS_WEB = (import.meta as any).env?.VITE_APP_IS_WEB === 'true';

// FLEXO-6087 (#1a): open a base64 PDF in a new browser tab (web). Mirrors the
// admin tables' openPdfWithTitle so the end-user invoices behave the same.
function openPdfBlobTab(b64: string, name: string) {
  const byteChars = atob(b64);
  const bytes = new Uint8Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) bytes[i] = byteChars.charCodeAt(i);
  const url = URL.createObjectURL(
    new Blob([bytes], { type: 'application/pdf' })
  );
  window.open(url, '_blank', 'noopener,noreferrer');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export function Billing({ view, selectedSites }: IBilling) {
  const {
    Billing__Bills,
    BillingData__Date,
    BillingData__PayedContainer,
    BillingData__Payed,
    BillingData__PayedIcon,
  } = styles;

  const loader = '/loaderLottie.json';

  const kpi = useSelector((state: RootState) => state.kpi);
  const metadata = useSelector((state: RootState) => (state as any).metadata);
  const params: any = useParams();
  const location = useLocation();
  const pathSegment = location.pathname.split('/')[1];
  const [bills, setBills] = useState<Array<BillingItem>>([]);
  const [billsLoadingState, setBillsLoadingState] = useState<boolean>(true);
  const [visibleBills, setVisibleBills] = useState<Array<BillingItem>>([]);
  const [isRefreshingInvoices, setIsRefreshingInvoices] =
    useState<boolean>(false);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isLoading = kpi?.KwickInvoicesAsyncStatus === 'PENDING';
  const hasError = kpi?.KwickInvoicesAsyncStatus === 'ERROR';
  const isSuccess = kpi?.KwickInvoicesAsyncStatus === 'SUCCESS';

  async function openBill(bill: BillingItem) {
    // FLEXO-6087 (#1a): fetch the actual PDF (file_base64) from the backend via
    // getInvoiceFile — the same flow the admin invoice tables use — then open it.
    // The getInvoices `url` is a non-public GCS console link (requires a Google
    // login) and must NOT be opened directly.
    const fileName = bill?.id ? `invoice_${bill.id}` : 'Invoice';
    let b64 = bill?.file_base64;
    if (!b64 && bill?.id && bill?.siteID) {
      const result: any = await dispatch(
        KPI.FetchKwickInvoiceFileAsync({
          invoiceNo: bill.id,
          siteId: bill.siteID,
        }) as any
      );
      b64 = result?.payload?.file_base64;
    }
    if (!b64) {
      // FLEXO-6133 #3: no silent no-op — tell the user the document is not
      // available (typical for "Due" invoices whose PDF does not exist yet).
      dispatch(
        ALERT.SetAlert({
          value: { type: 'BANNER_INVOICE_DOC_UNAVAILABLE' },
        } as any)
      );
      return;
    }
    if (IS_WEB) {
      openPdfBlobTab(b64, fileName);
    } else {
      await openDocument({
        file_base64: b64,
        fileName,
        mimeType: 'application/pdf',
      });
    }
  }

  // Build bills list from resolver data
  useEffect(() => {
    setBillsLoadingState(true);

    // Guard against a non-array payload (e.g. the backend's `{ message, invoices: [] }`
    // "no invoices" shape): calling `.map()` on it below would throw and blank the view.
    const invoices = Array.isArray(kpi?.kwickInvoices)
      ? (kpi?.kwickInvoices as KwickInvoice[])
      : [];
    if (!invoices || invoices.length === 0) {
      setBills([]);
      setBillsLoadingState(false);
      return;
    }

    const billsList: Array<BillingItem> = invoices
      .map((inv: KwickInvoice) => ({
        id: String(inv?.invoice_no || inv?.invoiceID || inv?.id || ''),
        emissionDate: inv?.emission_date,
        paid: Boolean(inv?.is_paid ?? inv?.paid),
        file_base64: inv?.file_base64,
        siteID: inv?.siteId || inv?.siteID,
        invoice_type: inv?.invoice_type,
      }))
      .sort((a, b) => {
        const da = a.emissionDate ? Date.parse(a.emissionDate) : 0;
        const db = b.emissionDate ? Date.parse(b.emissionDate) : 0;
        return db - da;
      });

    setBills(billsList);
    setBillsLoadingState(false);
  }, [kpi?.kwickInvoices]);

  useEffect(() => {
    if (bills && bills?.length > 0) {
      setBillsLoadingState(true);

      let _visibleBills: Array<BillingItem> = [];

      if (view === 'site' && selectedSites && selectedSites.length > 0) {
        _visibleBills = bills.filter(
          (bill: BillingItem) => bill.siteID === selectedSites[0]?.flexo_site_id
        );
      } else {
        _visibleBills = bills;
      }

      setVisibleBills(_visibleBills);
      setBillsLoadingState(false);
    } else {
      // No bills (e.g. an empty/filtered-out response): clear any previously
      // shown invoices so a stale list from the previous site/member isn't kept.
      setVisibleBills([]);
      setBillsLoadingState(false);
    }
  }, [bills, selectedSites, view]);

  return (
    <div className={styles.Billing}>
      <div className={Billing__Bills} style={{ position: 'relative' }}>
        {billsLoadingState === true ? (
          <Player autoplay loop src={loader} style={{ height: 20 }} />
        ) : (kpi as any)?.kwickInvoices === null ? (
          <div
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              minHeight: '60vh',
            }}
          >
            <div>
              <div style={{ opacity: 0.6, marginBottom: '0.75rem' }}>
                <IconWrapper iconName="alert" />
              </div>
              <p
                style={{
                  color: 'var(--hive-greyscale-600)',
                  margin: 0,
                  marginBottom: '0.75rem',
                }}
              >
                There was an issue loading your invoices
              </p>
              <button
                onClick={async () => {
                  if (isRefreshingInvoices) return;
                  try {
                    setIsRefreshingInvoices(true);
                    const siteIds = computeKwickInvoicesSiteIds(
                      metadata,
                      pathSegment,
                      params
                    );
                    const communityFilter = pathSegment?.includes('community') && !pathSegment?.includes('communities')
                      ? { communityId: params.id, sites: metadata?.entities?.sites || [] }
                      : undefined;
                    // Mirror KwickInvoicesResolver's member scoping so a retry on a
                    // /member/:id view stays scoped to that member (otherwise it would
                    // skip filterInvoicesByMemberPeriod and leak other occupants' invoices).
                    const memberFilter = pathSegment?.includes('member')
                      ? {
                          memberId: params.id,
                          sites: (metadata?.selectedEntity?.sites || []).map((site: any) => ({
                            siteId: site.siteId,
                            memberId: params.id,
                            memberChangelog: [{
                              memberId: params.id,
                              startDate: site.startDate || site.joinDate,
                              endDate: site.endDate || site.leaveDate,
                            }],
                          })),
                        }
                      : undefined;
                    await (dispatch as any)(
                      KPI.FetchKwickInvoicesAsync({ siteIds, communityFilter, memberFilter })
                    );
                  } finally {
                    setIsRefreshingInvoices(false);
                  }
                }}
                disabled={isRefreshingInvoices}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--hive-secondary-500)',
                  padding: 0,
                  margin: 0,
                  cursor: 'pointer',
                  fontSize: '16px',
                }}
              >
                {isRefreshingInvoices ? 'Refreshing…' : 'Refresh'}
              </button>
            </div>
          </div>
        ) : visibleBills && visibleBills?.length > 0 ? (
          visibleBills.map((bill, bill_i) => {
            // FLEXO-6133 #3: without an inline PDF or the (id, siteID) pair the
            // fetch can never be attempted — render the row non-interactive
            // instead of a pointer that silently does nothing.
            const canOpen = Boolean(
              bill?.file_base64 || (bill?.id && bill?.siteID)
            );
            return (
            <div
              className={styles.BillingData}
              key={`bill_${bill_i}`}
              onClick={canOpen ? () => openBill(bill) : undefined}
              style={{
                cursor: canOpen ? 'pointer' : 'default',
                opacity: canOpen ? 1 : 0.6,
                position: 'relative',
              }}
            >
              {isLoading && (
                <Shimmer />
              )}
              <span
                className={`${BillingData__Date} heading4 font-flexo-medium`}
              >
                {bill.invoice_type
                  ? t(
                      `widgets.tables.paymentsTable.invoiceType.${bill.invoice_type}`
                    )
                  : 'Energy: '}
                {bill.emissionDate ? formatHumanDate(bill.emissionDate) : ''}
              </span>
              <div className={BillingData__PayedContainer}>
                <span className={`${BillingData__Payed} paragraph`}>
                  {bill.paid
                    ? t('pages.profile.billing.paid')
                    : t('pages.profile.billing.due')}
                </span>
                <div className={`${BillingData__PayedIcon}`}>
                  <IconWrapper
                    iconName={
                      bill.paid ? 'tick_-encircled_active_secondary' : 'ellipse'
                    }
                  />
                </div>
              </div>
            </div>
            );
          })
        ) : (
          <p style={{ textAlign: 'center' }}>
            {t('pages.profile.billing.no_bills')}
          </p>
        )}
      </div>
    </div>
  );
}

export default Billing;
